// Updated: Added is_new toggle so admins can flag an event with a "NIEUW!" badge on the frontend
import React, { useState, useEffect } from 'react';
import Modal from '../../Modal';
import { Calendar, AlertTriangle } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

interface Event {
  id?: string;
  date: string;
  description: string;
  joinable?: boolean;
  is_new?: boolean;
  tikkie_url?: string | null;
  tikkie_price_label?: string | null;
  tikkie_url_set_at?: string | null;
  location?: string | null;
  timeslot?: string | null;
  note?: string | null;
}

interface EventModalProps {
  isOpen: boolean;
  onClose: () => void;
  event?: Event;
  onSuccess: () => void;
}

const EXPIRY_DAYS = 14;

const isTikkieExpired = (setAt: string | null | undefined): boolean => {
  if (!setAt) return false;
  const daysSince = Math.floor((Date.now() - new Date(setAt).getTime()) / (1000 * 60 * 60 * 24));
  return daysSince >= EXPIRY_DAYS;
};

const EventModal: React.FC<EventModalProps> = ({ isOpen, onClose, event, onSuccess }) => {
  const [formData, setFormData] = useState<Event>({
    date: '',
    description: '',
    joinable: false,
    is_new: false,
    tikkie_url: null,
    tikkie_price_label: null,
    location: null,
    timeslot: null,
    note: null,
  });
  const [feedback, setFeedback] = useState('');
  const [feedbackType, setFeedbackType] = useState<'success' | 'error'>('success');
  const tikkieExpired = isTikkieExpired(event?.tikkie_url_set_at);

  useEffect(() => {
    if (event) {
      setFormData({
        ...event,
        joinable: event.joinable ?? false,
        is_new: event.is_new ?? false,
        tikkie_url: event.tikkie_url ?? null,
        tikkie_price_label: event.tikkie_price_label ?? null,
        location: event.location ?? null,
        timeslot: event.timeslot ?? null,
        note: event.note ?? null,
      });
    } else {
      setFormData({
        date: '',
        description: '',
        joinable: false,
        is_new: false,
        tikkie_url: null,
        tikkie_price_label: null,
        location: null,
        timeslot: null,
        note: null,
      });
    }
  }, [event]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const effectiveTikkieUrl = tikkieExpired ? null : formData.tikkie_url?.trim() || null;
      const effectivePriceLabel = tikkieExpired ? null : formData.tikkie_price_label?.trim() || null;

      const payload: Record<string, unknown> = {
        date: formData.date,
        description: formData.description,
        joinable: formData.joinable,
        is_new: formData.is_new ?? false,
        tikkie_url: formData.joinable && effectiveTikkieUrl ? effectiveTikkieUrl : null,
        tikkie_price_label: formData.joinable && effectiveTikkieUrl && effectivePriceLabel ? effectivePriceLabel : null,
        location: formData.location?.trim() || null,
        timeslot: formData.timeslot?.trim() || null,
        note: formData.note?.trim() || null,
      };

      const hadTikkieUrl = event?.tikkie_url?.trim();
      const hasTikkieUrl = payload.tikkie_url;
      if (hasTikkieUrl && hasTikkieUrl !== hadTikkieUrl) {
        payload.tikkie_url_set_at = new Date().toISOString();
      } else if (!hasTikkieUrl) {
        payload.tikkie_url_set_at = null;
      }

      if (event?.id) {
        const { error } = await supabase
          .from('events')
          .update(payload)
          .eq('id', event.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('events')
          .insert([payload]);
        if (error) throw error;
      }

      setFeedback('Evenement succesvol opgeslagen');
      setFeedbackType('success');
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error saving event:', error);
      setFeedback('Er is een fout opgetreden');
      setFeedbackType('error');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="p-4">
        <h2 className="text-2xl font-medium text-gray-900 mb-6">
          {event?.id ? 'Evenement Bewerken' : 'Nieuw Evenement'}
        </h2>

        {feedback && (
          <div className={`mb-4 p-4 rounded-md ${
            feedbackType === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}>
            {feedback}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Datum
            </label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#E37C64] focus:ring-[#E37C64]"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Omschrijving
            </label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#E37C64] focus:ring-[#E37C64]"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Locatie (optioneel)
            </label>
            <input
              type="text"
              value={formData.location || ''}
              onChange={(e) => setFormData({ ...formData, location: e.target.value || null })}
              placeholder="bijv. Amsterdam, Café De Ysbreeker"
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#E37C64] focus:ring-[#E37C64]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tijd (optioneel)
            </label>
            <input
              type="text"
              value={formData.timeslot || ''}
              onChange={(e) => setFormData({ ...formData, timeslot: e.target.value || null })}
              placeholder="bijv. 14:00 - 16:00"
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#E37C64] focus:ring-[#E37C64]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Opmerking (optioneel)
            </label>
            <textarea
              value={formData.note || ''}
              onChange={(e) => setFormData({ ...formData, note: e.target.value || null })}
              placeholder="Extra informatie voor de deelnemer"
              rows={3}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#E37C64] focus:ring-[#E37C64]"
            />
          </div>
          <div className="flex items-center gap-3 pt-2">
            <input
              type="checkbox"
              id="joinable"
              checked={formData.joinable || false}
              onChange={(e) => setFormData({ ...formData, joinable: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300 text-[#5A130E] focus:ring-[#E37C64]"
            />
            <label htmlFor="joinable" className="text-sm font-medium text-gray-700">
              Online aanmeldbaar
            </label>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="is_new"
              checked={formData.is_new || false}
              onChange={(e) => setFormData({ ...formData, is_new: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300 text-[#5A130E] focus:ring-[#E37C64]"
            />
            <label htmlFor="is_new" className="text-sm font-medium text-gray-700">
              Toon "NIEUW!" badge
            </label>
          </div>
          {formData.joinable && (
            <div className="border-t border-gray-100 pt-4 space-y-3">
              {tikkieExpired && event?.tikkie_url && (
                <div className="flex items-start gap-2 p-3 rounded-lg border border-red-200 bg-red-50">
                  <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                  <p className="text-xs text-red-700 leading-relaxed">
                    De event-specifieke Tikkie is verlopen (ouder dan 14 dagen). De standaard Tikkie wordt nu automatisch gebruikt. Stel een nieuwe URL in om weer een afwijkende Tikkie te gebruiken.
                  </p>
                </div>
              )}
              <p className="text-xs text-gray-500">
                Laat onderstaande velden leeg om de standaard Tikkie te gebruiken. Vul alleen in bij een afwijkend bedrag.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Afwijkende Tikkie URL (optioneel)
                </label>
                <input
                  type="url"
                  value={tikkieExpired ? '' : (formData.tikkie_url || '')}
                  onChange={(e) => setFormData({ ...formData, tikkie_url: e.target.value || null })}
                  placeholder="https://tikkie.me/..."
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#E37C64] focus:ring-[#E37C64] text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prijslabel (optioneel)
                </label>
                <input
                  type="text"
                  value={tikkieExpired ? '' : (formData.tikkie_price_label || '')}
                  onChange={(e) => setFormData({ ...formData, tikkie_price_label: e.target.value || null })}
                  placeholder="bijv. EUR 89"
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#E37C64] focus:ring-[#E37C64] text-sm"
                />
              </div>
            </div>
          )}
          <div className="flex justify-between pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:text-gray-900 bg-gray-100 rounded-full"
            >
              Annuleren
            </button>
            <button
              type="submit"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#5A130E] hover:bg-[#5A130E]/90"
            >
              <Calendar className="w-4 h-4 mr-2" />
              {event?.id ? 'Bijwerken' : 'Toevoegen'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default EventModal;